import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { User, Role, Permission } from '@/types/auth';
import { toast } from 'sonner';
import { ADMIN_API_BASE_URL } from '@/api-services/config';
import { adminApiClient } from '@/api-services/hooks/adminApiClient';
import { riderApiClient } from '@/api-services/hooks/riderApiClient';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string, type?: string, rememberMe?: boolean) => Promise<boolean>;
  logout: () => void;
  register: (userData: any, role: string) => Promise<boolean>;
  resetPassword: (email: string) => Promise<boolean>;
  hasPermission: (resource: string, action: string) => boolean;
  hasRole: (roleName: string) => boolean;
  refreshToken: () => Promise<boolean>;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'REFRESH_TOKEN'; payload: User }
  | { type: 'INITIAL_CHECK_COMPLETE' };

// Mock user database - in production, this would be API calls
const mockUsers: User[] = [
  {
    id: '1',
    name: 'Super Admin',
    email: 'admin@aboosto.com',
    firstName: 'Super',
    lastName: 'Admin',
    role: 'super_admin',
    roleId: 'super_admin',
    roles: ['ROLE_SUPER_ADMIN'],
    status: 'active',
    emailVerified: true,
    phoneVerified: true,
    permissions: [
      'create_user', 'read_user', 'update_user', 'delete_user',
      'create_country', 'read_country', 'update_country', 'delete_country',
      'create_state', 'read_state', 'update_state', 'delete_state',
      'create_city', 'read_city', 'update_city', 'delete_city',
      'system_settings', 'view_logs', 'manage_permissions',
      'create', 'read', 'update', 'delete', 'read_analytics', 'read_reports', 'export_data'
    ],
    profilePicture: '',
    phone: '+1234567890',
    phoneNumber: '+1234567890',
    dateOfBirth: '1985-01-01',
    address: '123 Admin St, New York, NY 10001',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: '2',
    name: 'Operations Manager',
    email: 'manager@aboosto.com',
    firstName: 'Operations',
    lastName: 'Manager',
    role: 'admin',
    roleId: 'admin',
    roles: ['ROLE_ADMIN'],
    status: 'active',
    emailVerified: true,
    phoneVerified: true,
    permissions: [
      'create_user', 'read_user', 'update_user',
      'create_country', 'read_country', 'update_country',
      'create_state', 'read_state', 'update_state',
      'create_city', 'read_city', 'update_city',
      'create', 'read', 'update', 'read_analytics', 'read_reports'
    ],
    profilePicture: '',
    phone: '+1234567891',
    phoneNumber: '+1234567891',
    dateOfBirth: '1990-05-15',
    address: '456 Manager Ave, Los Angeles, CA 90001',
    createdAt: '2024-01-15T00:00:00Z',
    updatedAt: '2024-01-15T00:00:00Z'
  },
  {
    id: '3',
    name: 'John Driver',
    email: 'driver@aboosto.com',
    firstName: 'John',
    lastName: 'Driver',
    role: 'driver',
    roleId: 'driver',
    roles: ['ROLE_DRIVER'],
    status: 'active',
    emailVerified: true,
    phoneVerified: true,
    permissions: [
      'read', 'update', 'read_booking', 'update_booking', 'read_user'
    ],
    profilePicture: '',
    phone: '+1234567892',
    phoneNumber: '+1234567892',
    dateOfBirth: '1992-08-20',
    address: '789 Driver Rd, Chicago, IL 60007',
    createdAt: '2024-02-01T00:00:00Z',
    updatedAt: '2024-02-01T00:00:00Z'
  },
  {
    id: '4',
    name: 'Jane Passenger',
    email: 'passenger@aboosto.com',
    firstName: 'Jane',
    lastName: 'Passenger',
    role: 'passenger',
    roleId: 'passenger',
    roles: ['ROLE_PASSENGER'],
    status: 'active',
    emailVerified: true,
    phoneVerified: true,
    permissions: [
      'create', 'read', 'update', 'read_booking', 'update_booking', 'read_user'
    ],
    profilePicture: '',
    phone: '+1234567893',
    phoneNumber: '+1234567893',
    dateOfBirth: '1995-03-10',
    address: '321 Passenger Blvd, Miami, FL 33101',
    createdAt: '2024-02-15T00:00:00Z',
    updatedAt: '2024-02-15T00:00:00Z'
  }
];

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    case 'REFRESH_TOKEN':
      return {
        ...state,
        user: action.payload
      };
    case 'INITIAL_CHECK_COMPLETE':
      return {
        ...state,
        isLoading: false
      };
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Auto-logout on 403 Forbidden responses from any API client
  useEffect(() => {
    const handleForbidden = () => {
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('auth_user');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('authToken');
      adminApiClient.clearAuthToken();
      riderApiClient.clearAuthToken();
      dispatch({ type: 'LOGOUT' });
      window.location.href = '/login';
    };
    window.addEventListener('auth:forbidden', handleForbidden);
    return () => window.removeEventListener('auth:forbidden', handleForbidden);
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    // Check both localStorage (rememberMe) and sessionStorage (session only)
    const storedUser = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user');
    const storedToken = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

    if (storedUser && storedToken) {
      try {
        const user = JSON.parse(storedUser);
        // In production, you'd validate the token with your backend
        dispatch({ type: 'AUTH_SUCCESS', payload: user });
      } catch (error) {
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_token');
        sessionStorage.removeItem('auth_user');
        sessionStorage.removeItem('auth_token');
        // Set loading to false if authentication check fails
        dispatch({ type: 'INITIAL_CHECK_COMPLETE' });
      }
    } else {
      // Set loading to false if no stored session found
      dispatch({ type: 'INITIAL_CHECK_COMPLETE' });
    }
  }, []);

  const login = async (email: string, password: string, type: string = 'user', rememberMe: boolean = false): Promise<boolean> => {
    dispatch({ type: 'AUTH_START' });

    try {
      // API requires POST method, not GET
      // Use proxy path so requests go through Vite dev server → no CORS (same origin)
      const loginUrl = `${ADMIN_API_BASE_URL}/api/admin/login`;

      // Try JSON format first (most modern APIs prefer this)
      let response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userName: email,
          password: password,
          type: type,
        }),
        credentials: 'include',
      });

      // If JSON fails with 415 or 403, try form-urlencoded format (some Spring Boot APIs prefer this)
      if (!response.ok && (response.status === 415 || response.status === 403)) {
        const formData = new URLSearchParams();
        formData.append('userName', email);
        formData.append('password', password);
        formData.append('type', type);

        response = await fetch(loginUrl, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString(),
          credentials: 'include',
        });
      }

      if (!response.ok) {
        // Try to parse error response
        let errorMessage = 'Login failed';
        let errorDetails: any = null;
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            errorDetails = await response.json();
            errorMessage = errorDetails.message || errorDetails.error || errorDetails.details || `HTTP ${response.status}: ${response.statusText}`;
          } else {
            const text = await response.text();
            errorMessage = text || `HTTP ${response.status}: ${response.statusText}`;
          }
        } catch (parseError) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        
        dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
        toast.error(`Login failed: ${errorMessage}`);
        return false;
      }

      // Parse successful response
      const responseData = await response.json();
      
      // Extract token from response (API returns user object directly, not wrapped in status/data)
      const token = responseData.token;
      
      if (!token) {
        dispatch({ type: 'AUTH_FAILURE', payload: 'No token received from server' });
        toast.error('Login failed: No authentication token received');
        return false;
      }
      
      // Verify response has required fields
      if (!responseData.email && !responseData.userName) {
        const errorMessage = 'Invalid response from server: missing email or username';
        dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
        toast.error(errorMessage);
        return false;
      }

      // Decode JWT token to extract user information
      let decodedToken: any = null;
      try {
        // JWT tokens have 3 parts separated by dots: header.payload.signature
        const parts = token.split('.');
        if (parts.length === 3) {
          // Decode the payload (second part)
          const payload = parts[1];
          // Add padding if needed for base64 decoding
          const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
          const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
          decodedToken = JSON.parse(decodedPayload);
        }
      } catch (decodeError) {
        // Silently handle decode error
      }

      // Extract email from token (sub field) or use the email from login form
      const userEmail = decodedToken?.sub || email;
      const username = userEmail.split('@')[0];

      // Map API response to User type based on AppUserVO from swagger.json
      const apiUser = responseData;

      const extractRoles = (input: any): string[] => {
        if (!input) return [];
        if (Array.isArray(input)) return input.filter(Boolean).map(String);
        return [String(input)];
      };

      const apiRoles = extractRoles(apiUser.roles);
      const tokenRoles = [
        ...extractRoles(decodedToken?.roles),
        ...extractRoles(decodedToken?.authorities),
        ...extractRoles(decodedToken?.roleNames),
      ];

      const combinedRoles = [...new Set([...apiRoles, ...tokenRoles])];

      // Helper function to normalize role names from Spring Security format (ROLE_ADMIN) to app format (admin)
      const normalizeRole = (role: string): string => {
        if (!role) return 'admin';
        // Remove ROLE_ prefix if present and convert to lowercase
        return role.replace(/^ROLE_/, '').toLowerCase();
      };

      // Use API response data directly if available, otherwise use decoded token data
      const apiRole = apiRoles.length ? normalizeRole(apiRoles[0]) : null;
      const decodedRole = decodedToken?.role ? normalizeRole(decodedToken.role) : null;
      const decodedRoleName = decodedToken?.roleName ? normalizeRole(decodedToken.roleName) : null;
      const primaryRole = apiRole || decodedRole || decodedRoleName || 'admin';

      const user: User = {
        id: apiUser.id || decodedToken?.sub || decodedToken?.userId || String(Date.now()),
        userName: apiUser.userName || decodedToken?.userName || username,
        email: apiUser.email || userEmail,
        name: apiUser.contactName || decodedToken?.name || username,
        firstName: apiUser.firstName || decodedToken?.firstName || decodedToken?.first_name || username,
        lastName: apiUser.lastName || decodedToken?.lastName || decodedToken?.last_name || '',
        role: primaryRole,
        roleId: primaryRole,
        roles: combinedRoles.length ? combinedRoles : [`ROLE_${primaryRole.toUpperCase()}`],
        status: apiUser.activated ? 'active' : 'inactive',
        activated: apiUser.activated ?? decodedToken?.activated ?? true,
        isGoogleAccount: apiUser.isGoogleAccount ?? decodedToken?.isGoogleAccount ?? false,
        emailVerified: true, // Assume email is verified if login succeeds
        phoneVerified: decodedToken?.phoneVerified ?? false,
        permissions: apiUser.permissions || decodedToken?.permissions || decodedToken?.authorities || [],
        profilePicture: decodedToken?.profilePicture || decodedToken?.profile_picture || '',
        phone: apiUser.contactNumber || decodedToken?.phone || decodedToken?.phoneNumber || '',
        phoneNumber: apiUser.contactNumber || decodedToken?.phoneNumber || decodedToken?.phone || '',
        dateOfBirth: decodedToken?.dateOfBirth || decodedToken?.date_of_birth || '',
        address: decodedToken?.address || '',
        createdAt: apiUser.lastLoginDate || (decodedToken?.iat ? new Date(decodedToken.iat * 1000).toISOString() : new Date().toISOString()),
        updatedAt: new Date().toISOString(),
      };

      // If the API already returned permissions, use them directly
      // Otherwise, fetch permissions from the assigned custom role
      const apiPermissions = Array.isArray(apiUser.permissions) ? apiUser.permissions.filter(Boolean) : [];
      try {
        const assignedRoleName = apiUser.rolesAssigned;
        if (assignedRoleName && apiPermissions.length === 0) {
          const rolesUrl = `${ADMIN_API_BASE_URL}/roles/list?page=0&size=1000`;
          const rolesResp = await fetch(rolesUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });

          if (rolesResp.ok) {
            const rolesData = await rolesResp.json();
            const rolesList = rolesData?.data?.content || rolesData?.content || (Array.isArray(rolesData?.data) ? rolesData.data : []);
            const matchedRole = rolesList.find((r: any) =>
              (r.roleName || '').toLowerCase() === assignedRoleName.toLowerCase()
            );

            if (matchedRole) {
              const grantedPerms: string[] = Array.isArray(matchedRole.permissions) ? matchedRole.permissions : [];
              const restrictedPerms: string[] = Array.isArray(matchedRole.restricted) ? matchedRole.restricted : [];

              // Set permissions = granted minus restricted
              const effectivePerms = grantedPerms.filter(p => !restrictedPerms.includes(p));
              user.permissions = effectivePerms;

              // Store the custom role name and restricted list for reference
              user.role = assignedRoleName.toLowerCase().replace(/\s+/g, '_');
              user.roleId = assignedRoleName;
              (user as any).rolesAssigned = assignedRoleName;
              (user as any).restrictedPermissions = restrictedPerms;
            }
          }
        }
      } catch (err) {
        // If role fetch fails, proceed with whatever permissions the login API returned
      }

      // Check if user account is active
      if (user.status !== 'active') {
        dispatch({ type: 'AUTH_FAILURE', payload: 'Account is not active' });
        toast.error('Account is not active. Please contact support.');
        return false;
      }

      // Store in localStorage if rememberMe, otherwise sessionStorage
      const storage = rememberMe ? localStorage : sessionStorage;
      // Clear the other storage to avoid conflicts
      if (rememberMe) {
        sessionStorage.removeItem('auth_user');
        sessionStorage.removeItem('auth_token');
        sessionStorage.removeItem('authToken');
      } else {
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('authToken');
      }
      storage.setItem('auth_user', JSON.stringify(user));
      storage.setItem('auth_token', token);
      storage.setItem('authToken', token); // Also store as authToken for API hooks compatibility
      storage.setItem("currency",responseData.currency)
      storage.setItem("maxFileSize",responseData.maxFileSize)

      // Store max file size from login response (in MB), default to 1MB
      const maxFileSize = apiUser.maxFileSize || responseData.maxFileSize || 1;
      storage.setItem('max_file_size_mb', String(maxFileSize));

      dispatch({ type: 'AUTH_SUCCESS', payload: user });

      const roleDisplayName = user.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
      toast.success(`Welcome back, ${user.firstName}! Logged in as ${roleDisplayName}`);

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      toast.error(errorMessage);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('auth_user');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('authToken');
    localStorage.removeItem('max_file_size_mb');
    sessionStorage.removeItem('max_file_size_mb');
    // Clear in-memory token cache from API client singletons
    adminApiClient.clearAuthToken();
    riderApiClient.clearAuthToken();
    dispatch({ type: 'LOGOUT' });
    toast.success('Logged out successfully');
  };

  const register = async (userData: any, role: string): Promise<boolean> => {
    dispatch({ type: 'AUTH_START' });

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Check if user already exists
      const existingUser = mockUsers.find(u => u.email.toLowerCase() === userData.email.toLowerCase());
      if (existingUser) {
        dispatch({ type: 'AUTH_FAILURE', payload: 'User already exists' });
        toast.error('An account with this email already exists');
        return false;
      }

      // Create new user
      const newUser: User = {
        id: Date.now().toString(),
        email: userData.email,
        userName: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: role as any,
        status: 'pending', // New users start as pending
        roles: [`ROLE_${String(role).toUpperCase()}`],
        permissions: [], // Will be assigned based on role
        profilePicture: '',
        phoneNumber: userData.phone || '',
        dateOfBirth: userData.dateOfBirth || '',
        address: userData.homeAddress || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // In production, you'd send this to your backend
      // For now, just simulate successful registration
      toast.success('Registration successful! Please wait for account approval.');

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      toast.error(errorMessage);
      return false;
    }
  };

  const resetPassword = async (email: string): Promise<boolean> => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const user = mockUsers.find(u => u.email.toLowerCase() === email.toLowerCase());
      if (!user) {
        toast.error('If an account exists with this email, you will receive a password reset link');
        return true; // Always return true to prevent user enumeration
      }

      toast.success('Password reset link sent to your email');
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send reset email';
      toast.error(errorMessage);
      return false;
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    if (!state.user) return false;

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));

      // Generate new token
      const token = `mock_token_${state.user.id}_${Date.now()}`;
      localStorage.setItem('auth_token', token);

      // Update user with new timestamp
      const updatedUser = {
        ...state.user,
        updatedAt: new Date().toISOString()
      };

      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      dispatch({ type: 'REFRESH_TOKEN', payload: updatedUser });

      return true;
    } catch (error) {
      return false;
    }
  };

  const hasPermission = (resource: string, action: string): boolean => {
    if (!state.user) return false;

    // Super admins have all permissions
    const normalizedUserRole = state.user.role ? String(state.user.role).replace(/^ROLE_/, '').toLowerCase() : '';
    const userRolesList = (state.user.roles ?? []).map((r: any) => String(r).replace(/^ROLE_/, '').toLowerCase());
    if (normalizedUserRole === 'super_admin' || userRolesList.includes('super_admin')) {
      return true;
    }

    // Check against restricted permissions first (explicit deny)
    const restrictedPerms: string[] = (state.user as any)?.restrictedPermissions ?? [];
    const requestedPerm = `${action}_${resource}`;
    if (restrictedPerms.includes(requestedPerm) || restrictedPerms.includes(resource) || restrictedPerms.includes(action)) {
      return false;
    }

    const permissions = (state.user.permissions ?? []).map((permission) => {
      if (typeof permission === 'string') {
        return permission;
      }

      if ('id' in permission && permission.id) {
        return permission.id;
      }

      if ('resource' in permission && 'action' in permission) {
        return `${permission.action}_${permission.resource}`;
      }

      return null;
    }).filter((value): value is string => Boolean(value));

    // If user has no permissions at all (empty array), deny access
    // This handles ROLE_USER users who haven't been assigned a custom role with permissions
    if (permissions.length === 0) {
      return false;
    }

    // Normalize all permissions to lowercase for comparison
    const normalizedPerms = permissions.map(p => p.toLowerCase());

    // Check for exact permission match (e.g., "view_dashboard")
    const exactPermission = `${action}_${resource}`.toLowerCase();
    if (normalizedPerms.includes(exactPermission)) return true;

    // Check for human-readable permission name (e.g., "View Dashboard")
    // Permissions from API come as names like "View Dashboard", "Create Users"
    const humanReadable = `${action} ${resource}`.toLowerCase();
    if (normalizedPerms.includes(humanReadable)) return true;

    // Check for general resource permission
    if (normalizedPerms.includes(resource.toLowerCase())) return true;

    // Check for general action permission
    if (normalizedPerms.includes(action.toLowerCase())) return true;

    // Check if any permission contains the resource name (partial match)
    const resourceLower = resource.toLowerCase();
    const actionLower = action.toLowerCase();
    if (normalizedPerms.some(p => p.includes(resourceLower) && p.includes(actionLower))) return true;

    return false;
  };

  const hasRole = (roleName: string): boolean => {
    if (!state.user) return false;

    const normalizeRole = (role: string) => role.replace(/^ROLE_/, '').toLowerCase();

    const requestedRole = normalizeRole(roleName);
    const userRoles = new Set<string>();

    if (state.user.role) {
      userRoles.add(normalizeRole(state.user.role));
    }

    (state.user.roles ?? []).forEach(role => {
      if (role) {
        userRoles.add(normalizeRole(role));
      }
    });

    return userRoles.has(requestedRole);
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    register,
    resetPassword,
    hasPermission,
    hasRole,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
